import {
  addDays,
  differenceInMinutes,
  format,
  formatISO as fISO,
} from 'date-fns'
import { DayMonthLabel, ISODate, NullableWithDayAndMonth } from 'types/common'

const MINUTES_IN_HOUR = 60

export const removeMillisecondsFromIso = (date: string) =>
  date.replace(/[.]\d+/, '')

export const optionsWithTime = { hour: '2-digit', minute: '2-digit' }
export const optionsDate = { day: '2-digit', month: '2-digit', year: 'numeric' }

/**
 * from a given date, return a string like this: MM-YYYY, 02-2021
 */
export const getMonthAndYearFromDate = (date?: string): string =>
  date ? format(new Date(date), 'MM-yyyy') : '---'

/**
 * from a given date, return a string like this: HH:MM, 15:55
 */
export const getTimeLabelFromDate = (date?: Date | string) =>
  date ? format(new Date(date), 'HH:mm') : '---'

/**
 * from a given date, return a string like this: DD-MM-YYYY HH:MM, 28-02-2021 15:55
 */
export const getDateAndTimeAsString = (date?: Date | string) =>
  date ? format(new Date(date), 'dd-MM-yyyy HH:mm') : '---'

/**
 * from a given date, return a string like this: DD-MM-YYYY, 28-02-2021
 */
export const getDateAsString = (date?: string | Date) =>
  date ? format(new Date(date), 'dd-MM-yyyy') : '---'

export const getLabelFromTime = (hours: number, minutes: number): TimeLabel => {
  const hour = (hours < 10 ? '0' : '') + hours
  const minute = (minutes < 10 ? '0' : '') + minutes
  return `${hour}:${minute}`
}

export const getTimeLabels = () => {
  const items = []
  for (let i = 0; i < 24; i++) {
    const hour = getLabelFromTime(i, 0)
    const andHalf = getLabelFromTime(i, 30)
    items.push({ id: hour, name: hour }, { id: andHalf, name: andHalf })
  }
  return items
}

export const getServiceEndDate = (startDate: Date, endDate: Date) =>
  startDate > endDate ? addDays(endDate, 1) : endDate

/**
 * Takes a baseLineDate and returns a function that compares the date argument
 * to the 'baseLineDate' and returns the time offset between the two in hours
 *
 * @param baseLineDate
 * @param date
 * @returns
 */
export const getHoursDiferenceFromDate = (baseLineDate: Date) => (date: Date) =>
  differenceInMinutes(date, baseLineDate) / MINUTES_IN_HOUR

/**
 * Takes the delimiting dates of two time ranges as arguments and calculates how much of the
 * second range passed overlaps witht he first time range
 *
 * @param rangeStart
 * @param rangeEnd
 * @param overlapRangeStart
 * @param overlapRangeEnd
 *
 * @returns the number of hours of the overlap of the second range over the first
 */
export const getOverlapWithTimeRangeDuration = (
  rangeStart: Date,
  rangeEnd: Date,
  overlapRangeStart: Date,
  overlapRangeEnd: Date
) => {
  const getDifferenceFromRangeStart = getHoursDiferenceFromDate(rangeStart)

  return Math.max(
    Math.min(
      getDifferenceFromRangeStart(overlapRangeEnd),
      getDifferenceFromRangeStart(rangeEnd)
    ) - Math.max(getDifferenceFromRangeStart(overlapRangeStart), 0),
    0
  )
}

export const formatISO = (date?: string): ISODate | undefined =>
  !!date ? fISO(new Date(date)) : undefined

export type TimeLabel = string

export const getDayAndMonthFromLabel = (
  l?: DayMonthLabel
): NullableWithDayAndMonth => {
  const decoded = l?.split('-')

  return {
    month: decoded?.[2] ?? '',
    day: decoded?.[3] ?? '',
  } as NullableWithDayAndMonth
}

export const getDayMonthLabel = (dayAndMonth: NullableWithDayAndMonth) => {
  const { day, month } = dayAndMonth

  if (!day || !month) {
    throw new Error(
      'ERROR: you are trying to build a dayMonth label with at least one empty string value, these should always be defined instead'
    )
  }

  return `--${month}-${day}`
}

export const addDaysToDayMonthLabel = (l: DayMonthLabel, days: number) =>
  format(addDays(new Date(l), days), '--MM-dd') as DayMonthLabel

export enum WeekDaysEnum {
  sunday,
  monday,
  tuesday,
  wednesday,
  thursday,
  friday,
  saturday,
}
